import type { Spec } from "@json-render/core";
import { REPORT_HEADER_BRIEFING_TITLE_PLACEHOLDER } from "@/components/ui/report/ReportHeader/ReportHeader";
import type { ReportComponentTheme } from "@/components/ui/report/reportPresentation";
import {
  isReportBannerImage,
  resolveReportContainerId,
} from "@/lib/report/prepareReportSpecForRender";
import {
  normalizeTemplateLayoutHexColor,
  validateTemplateLayout,
} from "@/lib/template-layout/validateTemplateLayout";
import { parseGoogleFontsStylesheetUrl } from "@/lib/template-studio/brandAssets";
import { resolveLayoutAssetPreviewUrl } from "@/lib/template-studio/layoutAssetPreviewUrl";
import {
  type ResolvedTemplateStudioReportTheme,
  resolveReportThemeFromLayoutTheme,
} from "@/lib/template-studio/reportThemeFromLayoutTheme";
import {
  TEMPLATE_LAYOUT_DOCUMENT_SCHEMA_VERSION,
  type TemplateLayoutDocument,
  type TemplateLayoutHexColor,
  type TemplateLayoutSpec,
  type TemplateLayoutSpecElement,
  type TemplateLayoutTheme,
  type TemplateSlot,
} from "@/types/template-layout/templateLayout.types";
import type { TemplateSlotContentType } from "@/types/template-layout/templateSlotContent.types";
import {
  getDefaultTemplateSlotInputMode,
  TEMPLATE_SLOT_CONTENT_TYPES,
} from "@/types/template-layout/templateSlotContent.types";

/** Canonical saveable Template Studio editor document. */
export type TemplateStudioDraft = TemplateLayoutDocument;

/** Compiled layout returned by generate-layout (glean compiled spec body). */
export interface TemplateStudioCompiledSpec {
  catalogVersion: string;
  root: string;
  elements: Record<string, TemplateLayoutSpecElement>;
  state?: Record<string, unknown>;
}

export interface TemplateStudioThemeHints {
  googleFont?: { family: string; stylesheetUrl: string } | null;
  brandColor?: string | null;
  componentTheme?: ReportComponentTheme;
}

/** Product default primary colour (naapp violet). */
const DEFAULT_TEMPLATE_STUDIO_PRIMARY_COLOR = "#7c3aed" as const;

const templateSlotContentTypeSet = new Set<string>(TEMPLATE_SLOT_CONTENT_TYPES);

const isTemplateSlotContentType = (
  value: string,
): value is TemplateSlotContentType => templateSlotContentTypeSet.has(value);

/** Default theme for a new draft before the user sets branding. */
export const createDefaultTemplateStudioTheme = (): TemplateLayoutTheme => {
  const primaryColor: TemplateLayoutHexColor =
    DEFAULT_TEMPLATE_STUDIO_PRIMARY_COLOR;

  return {
    primaryColor,
    chart: {
      version: 1,
      strategy: "monochromatic",
      baseColor: primaryColor,
    },
  };
};

const deriveSlotLabel = (
  elementId: string,
  element: TemplateLayoutSpecElement,
): string => {
  const title = element.props?.title;
  if (typeof title === "string" && title.trim().length > 0) {
    return title.trim();
  }

  if (element.type === "ReportDate") return "Report date";
  if (element.type === "ReportHeader") return "Report header";
  if (element.type === "ReportFooter") return "Report footer";
  if (element.type === "ReportHighlight") return "Report highlight";

  return elementId;
};

const deriveSlotConstraints = (
  element: TemplateLayoutSpecElement,
): TemplateSlot["constraints"] => {
  if (element.type !== "ReportSection") return undefined;

  const articles = element.props?.articles;
  if (!Array.isArray(articles) || articles.length === 0) return undefined;

  return { maxItems: articles.length };
};

/**
 * Derives persisted slot definitions from layout spec elements.
 * Slot ids match element ids so reopen/refine can target the same nodes.
 */
export const deriveTemplateSlotsFromSpec = (
  spec: TemplateLayoutSpec,
): TemplateSlot[] =>
  Object.entries(spec.elements)
    .flatMap(([elementId, element]) => {
      const contentType = element.type;
      if (!isTemplateSlotContentType(contentType)) return [];

      const constraints = deriveSlotConstraints(element);
      const slot: TemplateSlot = {
        id: elementId,
        label: deriveSlotLabel(elementId, element),
        contentType,
        inputMode: getDefaultTemplateSlotInputMode(contentType),
        ...(constraints !== undefined ? { constraints } : {}),
      };

      return [slot];
    })
    .sort((left, right) => left.id.localeCompare(right.id));

const resolveThemeHints = (
  priorTheme: TemplateLayoutTheme | undefined,
  hints: TemplateStudioThemeHints | undefined,
): TemplateLayoutTheme => {
  const baseTheme = priorTheme ?? createDefaultTemplateStudioTheme();
  const nextTheme: TemplateLayoutTheme = {
    ...baseTheme,
    chart: { ...baseTheme.chart },
  };

  if (hints?.componentTheme !== undefined) {
    nextTheme.reportSection = hints.componentTheme.reportSection;
    nextTheme.reportHighlight = hints.componentTheme.reportHighlight;
  }

  const googleFont =
    hints?.googleFont ??
    (baseTheme.fontStylesheetUrl != null
      ? parseGoogleFontsStylesheetUrl(baseTheme.fontStylesheetUrl)
      : null);

  if (googleFont != null) {
    nextTheme.fontFamily = googleFont.family;
    nextTheme.fontStylesheetUrl = googleFont.stylesheetUrl;
  }

  const brandColor = hints?.brandColor;
  if (brandColor != null) {
    const normalized = normalizeTemplateLayoutHexColor(
      brandColor,
    ) as TemplateLayoutHexColor;
    nextTheme.primaryColor = normalized;
    nextTheme.chart = {
      ...nextTheme.chart,
      baseColor: normalized,
    };
  }

  return nextTheme;
};

const stripRuntimeReportDateValues = (
  elements: Record<string, TemplateLayoutSpecElement>,
): Record<string, TemplateLayoutSpecElement> =>
  Object.fromEntries(
    Object.entries(elements).map(([elementId, element]) => {
      if (
        (element.type !== "ReportHeader" && element.type !== "ReportDate") ||
        element.props == null ||
        !("dateValue" in element.props)
      ) {
        return [elementId, element];
      }

      const props = { ...element.props };
      delete props.dateValue;
      return [elementId, { ...element, props }];
    }),
  );

const isReportTitleElement = (element?: TemplateLayoutSpecElement) =>
  element?.type === "ReportHeader" || element?.type === "ReportDate";

/**
 * Saves the top of the report in render order, because the email renders the saved order:
 * banner Images, then ReportHeader (or ReportDate without a header), then a table of contents, then the rest.
 * A ReportDate next to a ReportHeader is dropped, since the header shows the date.
 */
const orderReportTopElements = (
  spec: TemplateLayoutSpec,
  bannerImageUrls: readonly string[],
  bodyImageUrls: readonly string[],
): TemplateLayoutSpec => {
  const hasReportHeader = Object.values(spec.elements).some(
    (element) => element.type === "ReportHeader",
  );
  const droppedIds = new Set(
    hasReportHeader
      ? Object.keys(spec.elements).filter(
          (elementId) => spec.elements[elementId]?.type === "ReportDate",
        )
      : [],
  );
  const elements = Object.fromEntries(
    Object.entries(spec.elements)
      .filter(([elementId]) => !droppedIds.has(elementId))
      .map(([elementId, element]) => {
        const children = element.children?.filter((id) => !droppedIds.has(id));
        const src = String(element.props?.src);
        const isImage = element.type === "Image";
        // The user's placement wins over Herald's flag, in both directions.
        const isBodyImage = isImage && bodyImageUrls.includes(src);
        const isBanner =
          isImage &&
          !isBodyImage &&
          (element.props?.banner === true || bannerImageUrls.includes(src));
        return [
          elementId,
          {
            ...element,
            ...(children ? { children } : {}),
            // Banners run edge to edge, so their corners stay square.
            ...(isBanner
              ? { props: { ...element.props, banner: true, rounded: false } }
              : isBodyImage
                ? {
                    props: Object.fromEntries(
                      Object.entries(element.props ?? {}).filter(
                        ([name]) => name !== "banner",
                      ),
                    ),
                  }
                : {}),
          },
        ];
      }),
  );

  const containerId = resolveReportContainerId(spec.root, elements);
  const container = elements[containerId];
  if (container?.children) {
    const isBanner = (id: string) => isReportBannerImage(elements[id]);
    const isTableOfContents = (id: string) =>
      elements[id]?.type === "ReportTableOfContents";
    const children = container.children;
    elements[containerId] = {
      ...container,
      children: [
        ...children.filter(isBanner),
        ...children.filter((id) => isReportTitleElement(elements[id])),
        ...children.filter(isTableOfContents),
        ...children.filter(
          (id) =>
            !isBanner(id) &&
            !isReportTitleElement(elements[id]) &&
            !isTableOfContents(id),
        ),
      ],
    };
  }

  return { ...spec, elements };
};

/**
 * Builds a canonical draft from a generate-layout compiled spec.
 * Theme hints prefer the current prompt, then the prior draft theme.
 */
export const buildTemplateStudioDraftFromCompiledSpec = ({
  compiledSpec,
  priorDraft,
  themeHints,
  bannerImageUrls = [],
  bodyImageUrls = [],
}: {
  compiledSpec: TemplateStudioCompiledSpec;
  priorDraft?: TemplateStudioDraft | null;
  themeHints?: TemplateStudioThemeHints;
  /** Srcs of attached images the user asked to use as a banner. */
  bannerImageUrls?: readonly string[];
  /** Srcs of attached images the user asked to place inside the report, not as a banner. */
  bodyImageUrls?: readonly string[];
}): TemplateStudioDraft => {
  const spec = orderReportTopElements(
    {
      root: compiledSpec.root,
      elements: stripRuntimeReportDateValues(compiledSpec.elements),
      ...(compiledSpec.state !== undefined
        ? { state: compiledSpec.state }
        : {}),
    },
    bannerImageUrls,
    bodyImageUrls,
  );

  return {
    schemaVersion: TEMPLATE_LAYOUT_DOCUMENT_SCHEMA_VERSION,
    catalogVersion: compiledSpec.catalogVersion.trim(),
    spec,
    theme: resolveThemeHints(priorDraft?.theme, themeHints),
    slots: deriveTemplateSlotsFromSpec(spec),
  };
};

/** Spec snapshot sent as generate-layout `previous` (includes catalogVersion). */
export const getRefinementPreviousFromDraft = (
  draft: TemplateStudioDraft,
): TemplateStudioCompiledSpec & { schemaVersion: "1.0" } => {
  return {
    schemaVersion: "1.0",
    catalogVersion: draft.catalogVersion,
    root: draft.spec.root,
    elements: stripRuntimeReportDateValues(draft.spec.elements),
    ...(draft.spec.state !== undefined ? { state: draft.spec.state } : {}),
  };
};

/** Layout spec used by the Template Studio preview renderer. */
export const getDisplaySpecFromDraft = (draft: TemplateStudioDraft): Spec => ({
  ...draft.spec,
  elements: Object.fromEntries(
    Object.entries(draft.spec.elements).map(([elementId, element]) => [
      elementId,
      { ...element, props: element.props ?? {} },
    ]),
  ) as Spec["elements"],
});

/**
 * Overlays briefing configure slot inputs onto the display spec (e.g. footer disclaimer).
 * Footers show the edited text, including a cleared value, and never placeholder copy.
 */
export const applyBriefingSlotInputsToDisplaySpec = (
  displaySpec: Spec,
  slotInputs: Record<string, string>,
): Spec => {
  const elements = Object.fromEntries(
    Object.entries(displaySpec.elements).map(([elementId, element]) => {
      if (element.type !== "ReportFooter") return [elementId, element];
      const input = slotInputs[elementId];
      return [
        elementId,
        {
          ...element,
          props: {
            ...(element.props ?? {}),
            ...(input !== undefined ? { disclaimer: input } : {}),
            showPlaceholder: false,
          },
        },
      ];
    }),
  );

  return { ...displaySpec, elements } as Spec;
};

/** Shows each renamed ReportSection heading, which the contents list also reads; other sections keep the template title. */
export const applyBriefingSlotTitlesToDisplaySpec = (
  displaySpec: Spec,
  slotTitles: Record<string, string>,
): Spec => {
  const elements = Object.fromEntries(
    Object.entries(displaySpec.elements).map(([elementId, element]) => {
      const title = slotTitles[elementId];
      if (element.type !== "ReportSection" || !title) {
        return [elementId, element];
      }
      return [elementId, { ...element, props: { ...element.props, title } }];
    }),
  );

  return { ...displaySpec, elements } as Spec;
};

/** Shows each replaced image in its Image slot, resolving relative `/media` URLs; a slot without a URL keeps the template image. */
export const applyBriefingImageAssetsToDisplaySpec = (
  displaySpec: Spec,
  imageAssets: Record<string, { url: string }>,
): Spec => {
  const elements = Object.fromEntries(
    Object.entries(displaySpec.elements).map(([elementId, element]) => {
      const src = imageAssets[elementId]?.url;
      if (element.type !== "Image" || !src) return [elementId, element];
      return [
        elementId,
        {
          ...element,
          props: { ...element.props, src: resolveLayoutAssetPreviewUrl(src) },
        },
      ];
    }),
  );

  return { ...displaySpec, elements } as Spec;
};

/** Image src saved on the template for an Image slot. */
export const getTemplateImageSrc = (
  document: TemplateLayoutDocument,
  elementId: string,
): string => {
  const src = document.spec.elements[elementId]?.props?.src;
  return typeof src === "string" ? src : "";
};

/** Footer text saved on the template, used to prefill the briefing footer input. */
export const getTemplateFooterText = (
  document: TemplateLayoutDocument,
  elementId: string,
): string => {
  const props = document.spec.elements[elementId]?.props;
  const text = props?.disclaimer ?? props?.text;
  return typeof text === "string" ? text : "";
};

/** Briefing name used when the user leaves the name blank. */
export const DEFAULT_BRIEFING_NAME = "New Briefing";

/** Report header title saved on the template, ignoring the placeholder copy. */
export const getTemplateBriefingTitle = (
  document: TemplateLayoutDocument,
): string => {
  for (const element of Object.values(document.spec.elements)) {
    if (element.type !== "ReportHeader") continue;
    const title = element.props?.briefingTitle;
    if (typeof title !== "string") continue;
    const trimmed = title.trim();
    if (trimmed && trimmed !== REPORT_HEADER_BRIEFING_TITLE_PLACEHOLDER) {
      return trimmed;
    }
  }
  return "";
};

/** Shows the briefing name (or the default) as every report header title. */
export const applyBriefingNameToDisplaySpec = (
  displaySpec: Spec,
  briefingName: string,
): Spec => {
  const briefingTitle = briefingName.trim() || DEFAULT_BRIEFING_NAME;
  const elements = Object.fromEntries(
    Object.entries(displaySpec.elements).map(([elementId, element]) => [
      elementId,
      element.type === "ReportHeader"
        ? { ...element, props: { ...(element.props ?? {}), briefingTitle } }
        : element,
    ]),
  );

  return { ...displaySpec, elements } as Spec;
};

export const BRIEFING_SAMPLE_OUTPUT_CAPTION =
  "Sample output. Live editions use the section prompts set on your briefing";

const SAMPLE_OUTPUT_PLACEHOLDER_BODY =
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.";

/** First H1/H2 line in Markdown content, e.g. `## Executive Summary`. */
export const getMarkdownHeadlineLine = (
  content: unknown,
): string | undefined =>
  typeof content === "string"
    ? content.match(/^#{1,2}\s+\S.*$/m)?.[0]
    : undefined;

/** Keeps the saved H1/H2 headline and swaps the body for placeholder copy. */
const toSampleOutputMarkdown = (content: unknown): string =>
  [
    getMarkdownHeadlineLine(content),
    `_${BRIEFING_SAMPLE_OUTPUT_CAPTION}_`,
    SAMPLE_OUTPUT_PLACEHOLDER_BODY,
  ]
    .filter(Boolean)
    .join("\n\n");

/**
 * Marks report sections and Markdown blocks in the briefing preview as sample output.
 * ReportHighlight, charts, metrics, and tables are intentionally left uncaptioned (HR-1179).
 */
export const applySampleOutputCaptionToDisplaySpec = (
  displaySpec: Spec,
): Spec => {
  const elements = Object.fromEntries(
    Object.entries(displaySpec.elements).map(([elementId, element]) => {
      const props = element.props ?? {};
      if (element.type === "ReportSection") {
        return [
          elementId,
          {
            ...element,
            props: { ...props, caption: BRIEFING_SAMPLE_OUTPUT_CAPTION },
          },
        ];
      }
      if (element.type === "Markdown") {
        return [
          elementId,
          {
            ...element,
            props: { ...props, content: toSampleOutputMarkdown(props.content) },
          },
        ];
      }
      return [elementId, element];
    }),
  );

  return { ...displaySpec, elements } as Spec;
};

/** {@link resolveReportThemeFromLayoutTheme} for a draft's persisted theme. */
export const resolveReportThemeFromDraft = (
  draft: TemplateStudioDraft | null | undefined,
): ResolvedTemplateStudioReportTheme =>
  resolveReportThemeFromLayoutTheme(draft?.theme);

export const serializeTemplateStudioDraft = (
  draft: TemplateStudioDraft,
): string => JSON.stringify(draft);

export const parseTemplateStudioDraft = (
  serialized: string,
): TemplateStudioDraft | null => {
  try {
    const parsed: unknown = JSON.parse(serialized);
    const result = validateTemplateLayout(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
};
